/**
 * Admin Resources Section - Templates & Downloads with Category Selection
 * Flow: Select Category → View Category-Specific Products & Metrics
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import { 
  Package, DollarSign, Download, Users, ShoppingCart,
  TrendingUp, CheckCircle, Clock, Eye, FileText, Edit, 
  Trash2, Plus, Loader2, ArrowLeft, Zap, BookOpen, Calculator,
  Shield
} from 'lucide-react';

const AdminResourcesSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Category definitions for Resources
  const categories = {
    trucking: {
      name: 'Trucking Operations Packet',
      subtitle: 'Run Your Business',
      icon: Package,
      color: '#EC4899',
      description: 'Essential documents for trucking operations',
      products: [
        { id: 1, name: 'Load Planning Templates', price: 25, sales: 18, downloads: 42 },
        { id: 2, name: 'Maintenance Log Sheets', price: 25, sales: 22, downloads: 48 }
      ],
      stats: {
        totalProducts: 2,
        totalSales: 40,
        totalRevenue: 1000,
        totalDownloads: 90,
        avgRating: 4.8
      }
    },
    cdl: {
      name: 'CDL Success Packet',
      subtitle: 'Pass Your CDL',
      icon: FileText,
      color: '#D4AF37',
      description: 'Study guides and practice materials for CDL tests',
      popular: true,
      products: [
        { id: 3, name: 'CDL Study Guide', price: 25, sales: 28, downloads: 56 },
        { id: 4, name: 'Pre-Trip Inspection Checklist', price: 25, sales: 24, downloads: 48 },
        { id: 5, name: 'Road Test Prep Guide', price: 25, sales: 18, downloads: 36 }
      ],
      stats: {
        totalProducts: 3,
        totalSales: 70,
        totalRevenue: 1750,
        totalDownloads: 140,
        avgRating: 4.9
      }
    },
    freightbroker: {
      name: 'Freight Broker Packet',
      subtitle: 'Start Your Brokerage',
      icon: Calculator,
      color: '#8B5CF6',
      description: 'Templates and guides for freight brokers',
      products: [
        { id: 6, name: 'Broker-Carrier Agreement', price: 25, sales: 15, downloads: 32 },
        { id: 7, name: 'Shipper Contract Template', price: 25, sales: 12, downloads: 28 },
        { id: 8, name: 'Rate Confirmation Template', price: 25, sales: 18, downloads: 38 }
      ],
      stats: {
        totalProducts: 3,
        totalSales: 45,
        totalRevenue: 1125,
        totalDownloads: 98,
        avgRating: 4.7
      }
    },
    authority: {
      name: 'Authority Builder Packet',
      subtitle: 'Get Your Authority',
      icon: BookOpen,
      color: '#10B981',
      description: 'Complete authority application documents',
      products: [
        { id: 9, name: 'Authority Checklist', price: 25, sales: 22, downloads: 44 },
        { id: 10, name: 'BOC-3 Filing Guide', price: 25, sales: 16, downloads: 32 },
        { id: 11, name: 'Insurance Requirements Guide', price: 25, sales: 14, downloads: 28 }
      ],
      stats: {
        totalProducts: 3,
        totalSales: 52,
        totalRevenue: 1300,
        totalDownloads: 104,
        avgRating: 4.8
      }
    },
    compliance: {
      name: 'Compliance Packet',
      subtitle: 'Stay Compliant',
      icon: Shield,
      color: '#3B82F6',
      description: 'Filing templates and compliance checklists',
      products: [
        { id: 12, name: 'Driver Qualification File', price: 25, sales: 20, downloads: 40 },
        { id: 13, name: 'Drug & Alcohol Policy', price: 25, sales: 15, downloads: 30 },
        { id: 14, name: 'HOS Compliance Guide', price: 25, sales: 12, downloads: 24 }
      ],
      stats: {
        totalProducts: 3,
        totalSales: 47,
        totalRevenue: 1175,
        totalDownloads: 94,
        avgRating: 4.8
      }
    },
    dispatch: {
      name: 'Dispatch Packet',
      subtitle: 'Book More Loads',
      icon: TrendingUp,
      color: '#F59E0B',
      description: 'Tools and templates for dispatchers',
      products: [
        { id: 15, name: 'Rate Calculator Spreadsheet', price: 25, sales: 25, downloads: 50 },
        { id: 16, name: 'Load Tracking Template', price: 25, sales: 18, downloads: 36 },
        { id: 17, name: 'Carrier Packet Template', price: 25, sales: 20, downloads: 40 }
      ],
      stats: {
        totalProducts: 3,
        totalSales: 63,
        totalRevenue: 1575,
        totalDownloads: 126,
        avgRating: 4.9
      }
    }
  };

  // Overall stats
  const overallStats = {
    totalProducts: 17,
    totalSales: 317,
    totalRevenue: 7925,
    totalDownloads: 652,
    avgRating: 4.8
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setTimeout(() => setLoading(false), 500);
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout title="Resources" subtitle="Loading...">
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-[#EC4899] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  // Category Selection Screen
  if (!selectedCategory) {
    return (
      <AdminLayout 
        title="Queen B Resources" 
        subtitle="Templates & Downloads - Select a category to view products"
      >
        {/* Overall Summary */}
        <div className="bg-gradient-to-r from-[#EC4899]/20 to-[#EC4899]/5 border border-[#EC4899]/30 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-lg font-bold mb-1">Resources Overview</h2>
              <p className="text-white/50 text-sm">{overallStats.totalProducts} products across all categories</p>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#EC4899]">${(overallStats.totalRevenue / 1000).toFixed(1)}k</p>
                <p className="text-white/40 text-xs">Total Revenue</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{overallStats.totalSales}</p>
                <p className="text-white/40 text-xs">Total Sales</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#3B82F6]">{overallStats.totalDownloads}</p>
                <p className="text-white/40 text-xs">Downloads</p>
              </div>
            </div>
          </div>
        </div>

        {/* Category Selection Cards */}
        <h3 className="text-white font-bold mb-4">Select a Category to View Products</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(categories).map(([key, category]) => (
            <div
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`bg-[#121212] border p-6 cursor-pointer hover:bg-[#1a1a1a] transition-all relative ${
                category.popular ? 'border-[#10B981]' : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={`resources-category-${key}`}
            >
              {category.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#10B981] text-black text-xs font-bold px-3 py-1">BEST SELLER</span>
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded" style={{ backgroundColor: `${category.color}20` }}>
                  <category.icon className="w-6 h-6" style={{ color: category.color }} />
                </div>
                <div>
                  <p className="text-white font-bold">{category.name}</p>
                  <p className="text-white/50 text-xs">{category.subtitle}</p>
                </div>
              </div>

              <p className="text-white/50 text-sm mb-4">{category.description}</p>

              <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Products</span>
                  <span className="text-white font-bold">{category.stats.totalProducts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Revenue</span>
                  <span className="text-[#22C55E] font-bold">${category.stats.totalRevenue.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Sales</span>
                  <span className="text-white font-bold">{category.stats.totalSales}</span>
                </div>
              </div>

              <button 
                className="w-full mt-4 py-3 text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: `${category.color}20`, 
                  color: category.color,
                  border: `1px solid ${category.color}40`
                }}
              >
                View {category.name} →
              </button>
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  // Category-Specific Products Screen
  const category = categories[selectedCategory];

  return (
    <AdminLayout 
      title={category.name} 
      subtitle={category.subtitle}
    >
      {/* Back Button */}
      <button
        onClick={() => setSelectedCategory(null)}
        className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
        data-testid="resources-back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to All Categories
      </button>

      {/* Category Header */}
      <div className="border p-4 sm:p-6 mb-6" style={{ 
        backgroundColor: `${category.color}10`, 
        borderColor: `${category.color}40` 
      }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded" style={{ backgroundColor: `${category.color}20` }}>
              <category.icon className="w-8 h-8" style={{ color: category.color }} />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold">{category.name}</h2>
              <p className="text-white/50">{category.stats.totalProducts} products • {category.stats.totalSales} sales</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: category.color }}>${category.stats.totalRevenue.toLocaleString()}</p>
              <p className="text-white/40 text-xs">Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <StatCard icon={Package} label="Products" value={category.stats.totalProducts} color={category.color} onClick={() => setActiveTab('products')} />
        <StatCard icon={ShoppingCart} label="Total Sales" value={category.stats.totalSales} color="#8B5CF6" onClick={() => setActiveTab('products')} />
        <StatCard icon={DollarSign} label="Revenue" value={`$${category.stats.totalRevenue.toLocaleString()}`} color="#22C55E" onClick={() => setActiveTab('overview')} />
        <StatCard icon={Download} label="Downloads" value={category.stats.totalDownloads} color="#3B82F6" onClick={() => setActiveTab('overview')} />
        <StatCard icon={TrendingUp} label="Avg Rating" value={`${category.stats.avgRating}★`} color="#D4AF37" onClick={() => setActiveTab('overview')} />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'products', label: 'Products', icon: Package },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`resources-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-[#EC4899]/10 border border-[#EC4899]/30'
                : 'text-white/50 hover:text-white border border-transparent'
            }`}
            style={{ color: activeTab === tab.id ? category.color : undefined }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="bg-[#121212] border border-white/10 p-6">
            <h3 className="text-white font-bold mb-4">Category Description</h3>
            <p className="text-white/70">{category.description}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-white">{category.stats.totalProducts}</p>
              <p className="text-white/50 text-xs">Products</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#22C55E]">${category.stats.totalRevenue.toLocaleString()}</p>
              <p className="text-white/50 text-xs">Revenue</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#8B5CF6]">{category.stats.totalSales}</p>
              <p className="text-white/50 text-xs">Sales</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#3B82F6]">{category.stats.totalDownloads}</p>
              <p className="text-white/50 text-xs">Downloads</p>
            </div>
          </div>
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="bg-[#121212] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-white font-bold">{category.name} Products</h3>
            <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium" style={{ backgroundColor: `${category.color}20`, color: category.color }}>
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/50 text-xs uppercase p-3">Product</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Price</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Sales</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Downloads</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {category.products.map((product) => (
                  <tr key={product.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${category.color}10` }}>
                          <category.icon className="w-5 h-5" style={{ color: category.color }} />
                        </div>
                        <span className="text-white font-medium">{product.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-[#22C55E] font-medium">${product.price}</td>
                    <td className="p-3 text-white">{product.sales}</td>
                    <td className="p-3 text-white/70">{product.downloads}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button className="p-1 text-white/50 hover:text-white"><Eye className="w-4 h-4" /></button>
                        <button className="p-1 text-white/50 hover:text-white" style={{ color: category.color }}><Edit className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-[#121212] border border-white/10 p-4 sm:p-5 cursor-pointer hover:bg-[#1a1a1a] hover:border-white/20 transition-all"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-white/50 text-sm">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

export default AdminResourcesSection;
